import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, PaidOutlined, RefreshOutlined } from '@mui/icons-material';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import { useAdminSubscriptionPlansQuery } from '@/state/queries/admin/use-admin-subscription-plans-query';
import { useDeleteSubscriptionPlanMutation } from '@/state/mutations/admin-subscription-plans/use-delete-subscription-plan-mutation';
import SubscriptionPlanAdminFormModal from '@/components/admin-subscription-plans/subscription-plan-admin-form.modal';
import type { SubscriptionPlan } from '@/types/dtos/transactions/subscription-plan.dto';
import { useTranslations } from '@/i18n/use-translations';

const ENTITY_FILTER = ['', 'Organization', 'Event'] as const;

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(n);
}

/** Main product lines (+ any parent keys referenced by add-ons). */
function productKeyFilterOptions(plans: SubscriptionPlan[]): string[] {
  const keys = new Set<string>();
  for (const p of plans) {
    const pk = p.subscriptionProductKey?.trim();
    if (pk && !p.requiresParent) {
      keys.add(pk);
    }
    const parent = p.parentProductKey?.trim();
    if (parent) {
      keys.add(parent);
    }
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

function planMatchesProductKeyFilter(plan: SubscriptionPlan, productKey: string): boolean {
  if (!productKey) return true;
  return plan.subscriptionProductKey === productKey || plan.parentProductKey === productKey;
}

export default function SubscriptionPlansAdminPage() {
  const t = useTranslations();
  const [filterEntity, setFilterEntity] = useState<'' | 'Organization' | 'Event'>('');
  const [filterProductKey, setFilterProductKey] = useState('');
  const { data, isLoading, error, refetch, isFetching } = useAdminSubscriptionPlansQuery(filterEntity);
  const plans = useMemo(() => data?.plans ?? [], [data?.plans]);
  const productKeyOptions = useMemo(() => productKeyFilterOptions(plans), [plans]);
  const effectiveProductKey = productKeyOptions.includes(filterProductKey) ? filterProductKey : '';
  const filteredPlans = useMemo(
    () => plans.filter((p) => planMatchesProductKeyFilter(p, effectiveProductKey)),
    [plans, effectiveProductKey],
  );
  const deleteMut = useDeleteSubscriptionPlanMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SubscriptionPlan | null>(null);

  const handleDelete = async (p: SubscriptionPlan) => {
    if (!p.id) {
      return;
    }
    const pk = p.subscriptionProductKey ? ` · ${p.subscriptionProductKey}` : '';
    if (!window.confirm(`Soft-delete plan ${p.tier} · ${p.entityType} · ${p.billingPeriod ?? 'MONTH'}${pk}?`)) {
      return;
    }
    try {
      await deleteMut.mutateAsync(p.id);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PaidOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('admin.subscriptionPlansCatalog.pageTitle')}
                  </Typography>
                  <IconButton size="small" aria-label="Refresh" onClick={() => refetch()} disabled={isFetching}>
                    {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
                  </IconButton>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditing(null);
                    setModalOpen(true);
                  }}
                >
                  {t('admin.subscriptionPlansCatalog.createPlan')}
                </Button>
              </Box>
            }
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, alignItems: { sm: 'center' } }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="filter-entity">{t('admin.subscriptionPlansCatalog.filterEntity')}</InputLabel>
              <Select
                labelId="filter-entity"
                label={t('admin.subscriptionPlansCatalog.filterEntity')}
                value={filterEntity}
                onChange={(e) => {
                  setFilterEntity(e.target.value as '' | 'Organization' | 'Event');
                  setFilterProductKey('');
                }}
              >
                <MenuItem value="">{t('admin.subscriptionPlansCatalog.allEntities')}</MenuItem>
                {ENTITY_FILTER.filter(Boolean).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="filter-product-key">{t('admin.subscriptionPlansCatalog.filterProductKey')}</InputLabel>
              <Select
                labelId="filter-product-key"
                label={t('admin.subscriptionPlansCatalog.filterProductKey')}
                value={effectiveProductKey}
                onChange={(e) => setFilterProductKey(e.target.value)}
              >
                <MenuItem value="">{t('admin.subscriptionPlansCatalog.allProductKeys')}</MenuItem>
                {productKeyOptions.map((key) => (
                  <MenuItem key={key} value={key}>
                    <Typography component="span" variant="body2" fontFamily="monospace" fontSize="0.85rem">
                      {key}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Card sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 0 }}>
              {error && (
                <Alert severity="error" sx={{ m: 2 }}>
                  {(error as Error).message}
                </Alert>
              )}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!isLoading && !filteredPlans.length && (
                <EmptyState text={t('admin.subscriptionPlansCatalog.emptyHint')} />
              )}
              {!isLoading && !!filteredPlans.length && (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tier</TableCell>
                        <TableCell>Entity</TableCell>
                        <TableCell>{t('admin.subscriptionPlansCatalog.tableProductKey')}</TableCell>
                        <TableCell>{t('admin.subscriptionPlansCatalog.tableCategory')}</TableCell>
                        <TableCell>{t('admin.subscriptionPlansCatalog.tableParent')}</TableCell>
                        <TableCell>Billing</TableCell>
                        <TableCell align="right">Price €</TableCell>
                        <TableCell>Id</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPlans.map((p) => (
                        <TableRow
                          key={p.id ?? `${p.tier}-${p.entityType}-${p.billingPeriod}-${p.subscriptionProductKey ?? ''}`}
                          hover
                          sx={{ opacity: p.deleted ? 0.5 : 1 }}
                        >
                          <TableCell>
                            <Chip size="small" label={p.tier} />
                            {p.deleted ? <Chip size="small" label="Deleted" color="warning" sx={{ ml: 0.5 }} /> : null}
                          </TableCell>
                          <TableCell>{p.entityType}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                              {p.subscriptionProductKey ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{p.subscriptionCategory ?? '—'}</TableCell>
                          <TableCell>
                            {p.requiresParent ? (
                              <Stack component="span" direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                                <Chip size="small" label="Add-on" color="info" variant="outlined" />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ maxWidth: 200 }}
                                >
                                  {p.parentProductKey ? `↳ ${p.parentProductKey}` : '—'}
                                </Typography>
                              </Stack>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>{p.billingPeriod ?? 'MONTH'}</TableCell>
                          <TableCell align="right">{formatMoney(p.price)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                              {p.id ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label="Edit"
                              onClick={() => {
                                setEditing(p);
                                setModalOpen(true);
                              }}
                              disabled={p.deleted || !p.id}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete"
                              onClick={() => handleDelete(p)}
                              disabled={p.deleted || deleteMut.isPending || !p.id}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <SubscriptionPlanAdminFormModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setEditing(null);
            }}
            plan={editing}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, px: 1 }}>
            {t('admin.subscriptionPlansCatalog.footerNote')}
          </Typography>
        </Box>
      }
    />
  );
}
